import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, MonitorUp } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { secondsLeft } from '../../lib/share/peer';
import type { SessionDTO } from '../../lib/share/types';
import { Avatar } from '../Avatar';
import { useShare } from './ShareProvider';

/**
 * "{name} wants to share their screen with you": tarjeta fija arriba al centro (z 4100, 440 px,
 * sin backdrop) que vive 45 s y no bloquea nada: tiene que verse aunque haya un modal 4000 abierto.
 * Enter acepta, Esc rechaza — pero SOLO con el foco en la tarjeta o en el body: con el foco en el
 * "Save" de un modal, Enter tiene que guardar (no aceptar el share) y Esc cerrar el modal (no
 * rechazar en silencio con 30 s de cooldown). Por eso el botón Accept toma el foco al montar: el
 * teclado sigue sirviendo sin un oyente que se coma las teclas de toda la app. En ≤900 px es un
 * bottom-sheet con botones de 48 px.
 */
export function ShareRequestCard({ session }: { session: SessionDTO }) {
  const { t } = useI18n();
  const s = useShare();
  const [left, setLeft] = useState(() => secondsLeft(session.expiresAt, Date.now()));
  const acceptRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Foco inicial en Accept salvo que la persona esté escribiendo: no se le roba el cursor a un input.
    const el = document.activeElement as HTMLElement | null;
    const escribiendo = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    if (!escribiendo) acceptRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setLeft(secondsLeft(session.expiresAt, Date.now())), 500);
    return () => clearInterval(id);
  }, [session.expiresAt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      // Solo si el foco está en la tarjeta o en ningún control (body): nunca sobre un botón, un
      // link, un select, un input o un modal ajeno. Sin modificadores (⌘↵ es de otro).
      if (el && el !== document.body && !el.closest('.ss-request')) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.key === 'Enter') { e.preventDefault(); s.accept(session.id); }
      else if (e.key === 'Escape') { e.preventDefault(); s.decline(session.id); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [s, session.id]);

  const body = (
    <div className="ss-request" role="alertdialog" aria-live="assertive" aria-label={t('share.incomingTitle', { name: session.sharer.name })}>
      <div className="ss-request-row">
        <Avatar userId={session.sharer.id} name={session.sharer.name} size={40} noCall />
        <div className="ss-request-text">
          <b>{t('share.incomingTitle', { name: session.sharer.name })}</b>
          <span className="ss-request-hint">{t('share.incomingHint')}</span>
          <span className="ss-request-exp">{t('share.expiresIn', { s: left })}</span>
        </div>
        <MonitorUp size={18} strokeWidth={1.8} className="ss-request-ico" aria-hidden />
      </div>
      <div className="ss-request-actions">
        <button type="button" className="btn ghost ss-decline" onClick={() => s.decline(session.id)}>
          <X size={15} strokeWidth={2} /> {t('share.decline')}
        </button>
        <button type="button" className="btn ss-accept" ref={acceptRef} onClick={() => s.accept(session.id)}>
          <Check size={15} strokeWidth={2.2} /> {t('share.accept')}
        </button>
      </div>
    </div>
  );
  return createPortal(body, document.body);
}
